/**
 * Gift Economy — digital gift cards.
 * Backend:
 *   POST /api/gifts/create   -> mint a gift card
 *   POST /api/gifts/redeem   -> credit balance to wallet
 *   GET  /api/gifts/mine     -> {purchased, redeemed}
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Gift, Copy, Plus, Ticket, Check } from "lucide-react";
import { toast } from "sonner";

function money(n) { return `£${(Number(n) || 0).toFixed(2)}`; }

export default function Gifts() {
  const [purchased, setPurchased] = useState([]);
  const [redeemed, setRedeemed] = useState([]);
  const [creating, setCreating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [form, setForm] = useState({ amount_gbp: 25, recipient_name: "", recipient_email: "", message: "" });
  const [showCreated, setShowCreated] = useState(null);
  const [redeemCode, setRedeemCode] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/gifts/mine");
      setPurchased(r.data.purchased || []);
      setRedeemed(r.data.redeemed || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const amount = parseFloat(form.amount_gbp);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setCreating(true);
    try {
      const r = await api.post("/gifts/create", { ...form, amount_gbp: amount });
      setShowCreated(r.data);
      toast.success(`Gift card created: ${r.data.code}`);
      setForm({ amount_gbp: 25, recipient_name: "", recipient_email: "", message: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create gift");
    }
    setCreating(false);
  };

  const redeem = async () => {
    if (!redeemCode.trim()) return toast.error("Enter a code");
    setRedeeming(true);
    try {
      const r = await api.post("/gifts/redeem", { code: redeemCode.trim() });
      toast.success(`Credited ${money(r.data.credited_gbp)} to your wallet`);
      setRedeemCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redemption failed");
    }
    setRedeeming(false);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="gifts-page">
      <div>
        <div className="overline text-secondary flex items-center gap-2"><Gift className="w-3 h-3" /> The Gift Economy</div>
        <h1 className="text-3xl font-display font-light mt-1">Send the gift of repair</h1>
        <p className="mt-2 text-muted-foreground max-w-xl">
          Digital gift cards redeemable against any Treasurescare repair, plan, or spare-part. Recipient gets the full balance credited to their wallet.
        </p>
      </div>

      <Tabs defaultValue="create" className="w-full">
        <TabsList data-testid="gifts-tabs">
          <TabsTrigger value="create" data-testid="tab-create-gift"><Plus className="w-4 h-4 mr-1.5" /> Create</TabsTrigger>
          <TabsTrigger value="redeem" data-testid="tab-redeem-gift"><Ticket className="w-4 h-4 mr-1.5" /> Redeem</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-my-gifts">My cards</TabsTrigger>
        </TabsList>

        <TabsContent value="create">
          <Card>
            <CardHeader><CardTitle className="text-base">Mint a new gift card</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Amount (£)</Label>
                <Input type="number" min={10} max={500} step={5} value={form.amount_gbp}
                       onChange={(e) => setForm(f => ({ ...f, amount_gbp: e.target.value }))}
                       data-testid="gift-amount-input" />
                <div className="text-xs text-muted-foreground mt-1">£10 minimum · £500 maximum</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Recipient name (optional)</Label>
                  <Input value={form.recipient_name} onChange={(e) => setForm(f => ({ ...f, recipient_name: e.target.value }))} data-testid="gift-recipient-name" />
                </div>
                <div>
                  <Label>Recipient email (optional)</Label>
                  <Input type="email" value={form.recipient_email} onChange={(e) => setForm(f => ({ ...f, recipient_email: e.target.value }))} data-testid="gift-recipient-email" />
                </div>
              </div>
              <div>
                <Label>Message (optional)</Label>
                <Textarea rows={2} maxLength={280} value={form.message}
                          onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                          placeholder="Happy birthday! Look after your gadgets ✨"
                          data-testid="gift-message" />
              </div>
              <Button onClick={create} disabled={creating} data-testid="gift-create-btn">
                {creating ? "Creating…" : "Create gift card"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="redeem">
          <Card>
            <CardHeader><CardTitle className="text-base">Redeem a gift code</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Gift code</Label>
                <Input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                       placeholder="XXXX-XXXX-XXXX" className="font-mono uppercase tracking-widest" data-testid="gift-redeem-input" />
              </div>
              <Button onClick={redeem} disabled={redeeming} data-testid="gift-redeem-btn">
                {redeeming ? "Redeeming…" : "Redeem to wallet"}
              </Button>
              <div className="text-xs text-muted-foreground">
                Balance is credited to your Treasurescare Wallet and can be used against any invoice.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mine">
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Cards you purchased ({purchased.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {purchased.length === 0 ? (
                  <div className="text-center py-6 text-sm text-muted-foreground">You haven't sent any gifts yet.</div>
                ) : purchased.map(g => (
                  <div key={g.id} className="flex items-center justify-between border border-border rounded-lg p-3" data-testid={`purchased-${g.id}`}>
                    <div>
                      <div className="font-mono text-sm">{g.code}</div>
                      <div className="text-xs text-muted-foreground">
                        {money(g.amount_gbp)} · {g.recipient_name || g.recipient_email || "Unassigned"} · {new Date(g.created_at).toLocaleDateString("en-GB")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={g.status === "active" ? "default" : "outline"}>{g.status}</Badge>
                      <Button variant="ghost" size="icon" onClick={() => copyCode(g.code)}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Cards you redeemed ({redeemed.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {redeemed.length === 0 ? (
                  <div className="text-center py-6 text-sm text-muted-foreground">No redemptions yet.</div>
                ) : redeemed.map(g => (
                  <div key={g.id} className="flex items-center justify-between border border-border rounded-lg p-3">
                    <div>
                      <div className="font-mono text-sm">{g.code}</div>
                      <div className="text-xs text-muted-foreground">
                        Redeemed {money(g.amount_gbp)} on {new Date(g.redeemed_at).toLocaleDateString("en-GB")}
                      </div>
                    </div>
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!showCreated} onOpenChange={(o) => !o && setShowCreated(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Gift card ready</DialogTitle></DialogHeader>
          {showCreated && (
            <div className="space-y-4">
              <div className="bg-primary text-primary-foreground rounded-xl p-6 relative overflow-hidden">
                <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-secondary/25 blur-3xl" />
                <div className="relative">
                  <div className="overline text-secondary">Treasurescare gift</div>
                  <div className="mt-1 font-display text-4xl font-light">{money(showCreated.amount_gbp)}</div>
                  <div className="mt-4 font-mono text-lg tracking-widest bg-black/30 rounded-md px-3 py-2 inline-block" data-testid="new-gift-code">
                    {showCreated.code}
                  </div>
                </div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => copyCode(showCreated.code)}>
                <Copy className="w-4 h-4 mr-2" /> Copy code
              </Button>
            </div>
          )}
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </div>
  );
}
